/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import _ from "underscore";
import util from "../../../util/util.js";
import moment from "dayjs";
import Papa from "papaparse";
import ko from "knockout";
import birdata from "../../../api.js";
import customParseFormat from 'dayjs/plugin/customParseFormat';
moment.extend(customParseFormat); // todo: rename to dayjs

function SurveyImporter(params) {
	var self = this;

	var species = params.species;
	var lookups = params.lookups;

	self.source = ko.observable();
	self.sources = ko.observable(filterSources(lookups.source));

	self.ready = ko.observable(true);

	self.importSpecificationURL = {
		"surveys-birdata": "http://birdata.birdlife.org.au/survey-import-data-specification",
		"surveys-sop": "http://birdata.birdlife.org.au/survey-import-data-specification-sydney-olympic-park",
		"surveys-s2020": "http://birdata.birdlife.org.au/survey-import-data-specification-shorebirds-2020"
	}[params.code];

	self.selectFileOptions = {
		accept: 'text/csv',
		multiple: true
	};

	self.showFileSelect = ko.pureComputed(function() {
		return !!self.source();
	});

	self.showAddSourceDialog = ko.observable(false);

	self.newSourceName = ko.observable('');
	self.newSourceDescription = ko.observable('');

	self.canAddSource = ko.pureComputed(function() {
		return self.newSourceName().trim() && self.newSourceDescription().trim();
	});

	function toCode(str) {
		return str.toLowerCase().replace(/[^a-z0-9]/g, ' ').trim().split(/\s+/).join('_').substr(0, 100);
	}

	// We really don't want people importing data and claiming that it originated from Birdata
	function filterSources(sources) {
		var filtered = sources.filter(function(s) {
			return s.code != 'birdata' && s.code != 'birdata_1';
		});
		return _.sortBy(filtered, 'name');
	}

	self.addSource = function() {
		var source = {
			name: self.newSourceName().trim(),
			description: self.newSourceDescription().trim()
		};

		source.code = toCode(source.name);

		birdata.addLookupItem('source', source).then(function() {
			self.showAddSourceDialog(false);
			return birdata.lookup('source');
		}).then(function(sources) {
			// Refresh source list and update selection
			self.sources(filterSources(sources));
			self.source(_.findWhere(sources, { code: source.code }));
		}).catch(function(e) {
			console.log(e);
			alert("Failed to add source");
			return e;
		});
	}

	self.updateMeta = function(meta) {
		meta.source = self.source() && self.source().code;
	};

	self.loadMeta = function(json) {
		self.source(_.findWhere(self.sources(), { code: json.meta.source }));
		return Promise.resolve(true);
	};

	function parseCSV(file) {
		return new Promise(function(resolve, reject) {
			Papa.parse(file, {
				header: true,
				preview: 1000,
				skipEmptyLines: true,
				complete: resolve,
				error: reject
			});
		});
	}

	function containsProperties(obj, props) {
		return _.all(props, function(p) { return obj.hasOwnProperty(p); });
	}

	function checkColumns(object, required, optional) {
		var issues = [];
		var columns = _.keys(object);

		var unrecognized = _.difference(columns, required, optional);
		if(params.code != 'surveys-bnb') {
			_.each(unrecognized, function(col) {
				issues.push({
					message: "Unrecognized column (will be ignored): " + col,
					type: "Warning"
				});
			});
		}

		var missing = _.difference(required, columns);
		_.each(missing, function(col) {
			issues.push({
				message: "Missing required column: " + col,
				type: "Error"
			});
		});

		var seen = [];
		_.each(columns, function(column) {
			if(_.contains(seen, column)) {
				issues.push({
					message: "Duplicate column: " + column,
					type: "Error"
				});
			}
			seen.push(column);
		});

		return issues;
	}


	function rowIssue(rowIssues, type, row, message) {
		var key = type + "," + message;

		if(key in rowIssues) {
			rowIssues[key].count++;
		} else {
			rowIssues[key] = {
				row: row,
				count: 1,
				message: message,
				type: type
			};
		}
	}

	function checkSurveyFile(fileModel) {
		var requiredColumns = [
			'SurveyID',
			'Latitude',
			'Longitude',
			'SurveyPointName',
			'StartDate',
			'SurveyType',
			'PrivateSurvey'];

		var optionalColumns = [
			'AccuracyInMetres',
			'FinishDate',
			'StartTime',
			'DurationInMinutes',
			'NumberOfObservers',
			'SurveyNotes',
			'AllSpeciesRecorded',
			'WaterLevel',
			'RaisersEdgeID',
			'BirdataUserID',
			'Email'];

		if(params.code == 'surveys-sop') {
			requiredColumns = _.without(requiredColumns, 'SurveyType');
			requiredColumns.push('SOPSurveyType');
		}

		if(params.code == 'surveys-s2020') {
			requiredColumns = _.without(requiredColumns, 'SurveyType');
			requiredColumns.push('AllShorebirdsVisibleCounted');
			requiredColumns.push('CompleteWaterbirdCount');
			optionaColumns.push('WindSpeed');
			optionaColumns.push('WindDirection');
			optionaColumns.push('Tide');
			optionaColumns.push('PartOfBiannualCount');
			optionaColumns.push('CompleteCount');
		}

		if(params.code == 'surveys-bnb') {
			requiredColumns = _.without(requiredColumns, 'SurveyType');
		}

		if(params.code == 'surveys-abc') {
			requiredColumns.push('Experience');
		}

		var issues = checkColumns(fileModel.parseResult.data[0], requiredColumns, optionalColumns);
		var seenIDs = {};
		var rowIssues = {};
		var errorCount = _.countBy(issues, 'type').Error || 0;

		var surveyTypes = _.pluck(lookups.survey_type, 'code');
		var waterLevels = _.pluck(lookups.water_level, 'code');

		var dateFormat;

		if(errorCount == 0) {
			_.each(fileModel.parseResult.data, function(row, rowNumber) {
				function iss(type, msg) {
					rowIssue(rowIssues, type, rowNumber, msg);
				}

				_.each(requiredColumns, function(col) {
					if(row[col] === "") {
						iss("Warning", col + " must not be blank (survey will not be imported)");
					}
				});

				if(row.SurveyType && !_.contains(surveyTypes, row.SurveyType)) {
					iss("Warning", "Unrecognized survey type: " + row.SurveyType + "(survey will not be imported)");
				}

				if(row.SurveyID) {
					if(seenIDs.hasOwnProperty(row.SurveyID)) {
						iss("Error", "Duplicate surveyID: " + row.SurveyID);
					}
					seenIDs[row.SurveyID] = true;
				}

				if(row.Latitude == 0 || row.Latitude < -90 || row.Latitude > 90 ||
					row.Longitude == 0 || row.Longitude < -180 || row.Longitude > 180) {
					iss("Warning", "Invalid coordinates (survey will not be imported)");
				}

				var dates = _.map([row.StartDate, row.FinishDate], function(date) {
					if(!date) return null;

					if(!dateFormat) {
						dateFormat = _.find(["DD/MM/YY", "DD/MM/YYYY", "D/M/YY", "D/M/YYYY", "YYYY-MM-DD"], function(format) {
							var d = moment(date, format, true); // strict parsing option
							console.log(d.isValid());
							console.log(d.year());
							return d.isValid() && d.year() >= 1000;
						});
						if(dateFormat) {
							iss("Info", "Date format detected: " + dateFormat);
						} else {
							iss("Error", "Invalid date (must be DD/MM/YYYY or YYYY-MM-DD)");
						}
					}

					if(dateFormat) {
						var d = moment(date, dateFormat);
						if(!d.isValid()) {
							iss("Error", "Inconsistent date format - entire file must either use DD/MM/YYYY or YYYY-MM-DD");
						} else if(d.year() < 1900) {
							iss("Warning", "Date is too old (before 1900)");
						} else if(d.diff(moment(), 'day') > 1) {
							iss("Warning", "Date is in the future");
						}

						return d;
					}
				});

				var start = dates[0], finish = dates[1];
				if(start && finish && start.isValid() && finish.isValid()) {
					if(start.diff(finish) > 0) {
						iss("Warning", "Finish date is before start date (survey will not be imported)");
					}
				}

				if(row.StartTime) {
					if(row.FinishDate && row.StartDate != row.FinishDate) {
						iss("Warning", "Start time will be ignored for multi-day survey");
					}
					var parts = /^([0-9]{1,2}):([0-9]{2})(:([0-9]{2}))?$/;
					if(!parts) {
						iss("Warning", "Invalid start time");
					} else {
						var h = +parts[0];
						var m = +parts[1];
						var s = +parts[2];
						if(h > 23 || m > 59 || s > 59) {
							iss("Warning", "Invalid start time");
						}
					}
				}

				if(row.NumberOfObservers) {
					if(!util.isInt(row.NumberOfObservers) || +row.NumberOfObservers <= 0) {
						iss("Warning", "Invalid number of observers: " + row.NumberOfObservers);
					}
				}

				if(row.AllSpeciesRecorded) {
					switch(row.AllSpeciesRecorded.toLowerCase()) {
						case "true":
						case "false":
						case "yes":
						case "no":
							break;
						default:
							iss("Warning", "AllSpeciesRecorded must be either 'true' or 'false'");
					}
				}

				if(row.WetlandWaterLevel) {
					if(!_.contains(waterLevels, row.WetlandWaterLevel)) {
						iss("Warning", "Unrecognized water level: " + row.WetlandWaterLevel);
					}
				}
			});
		}

		_.chain(rowIssues).values().sortBy('row').each(function(issue) {
			var message = "Row " + (issue.row + 1) + ": " + issue.message;
			if(issue.count > 1) {
				message = message + " (" + issue.count + " occurences in file)";
			}
			issues.push({
				message: message,
				type: issue.type
			});
		});

		_.each(issues, function(i) {
			i.message = fileModel.file.name + ": " + i.message;
		});

		return issues;
	}

	function checkSightingFile(fileModel) {
		var requiredColumns = ['SurveyID', 'Species', 'Count'];
		var optionalColumns = ['BreedingActivity', 'SightingNotes', 'Subspecies', 'VettingStatus', 'VettingNotes'];

		if(params.code == 'surveys-sop') {
			requiredColumns.push('DetectionMethod');
			optionalColumns.push('Behaviour');
			optionalColumns.push('LifeStage');
			optionalColumns.push("VitalStatus");
			optionalColumns.push("Sex");
			optionalColumns.push("WeightInGrams");
			optionalColumns.push("LengthInMillimetres");
			optionalColumns.push("LegLengthInMillimetres");
			optionalColumns.push("SnoutLengthInMillimetres");
			optionalColumns.push("NuptialPads");
			optionalColumns.push("TagNumber");
			optionalColumns.push("Recapture");
		}

		var issues = checkColumns(fileModel.parseResult.data[0], requiredColumns, optionalColumns);

		var breedingActivities = _.pluck(lookups.breeding_activity, 'code');
		var speciesNames = _.pluck(species, 'common_name').map(name => name.toLowerCase());

		var seenIDs = {};
		var rowIssues = {};
		var errorCount = _.countBy(issues, 'type').Error || 0;

		var allowMultipleSightingsPerSpecies = (params.code === 'surveys-sop' || params.code === 'surveys-bnb');

		if(errorCount == 0) {
			_.each(fileModel.parseResult.data, function(row, rowNumber) {

				function iss(type, msg) {
					rowIssue(rowIssues, type, rowNumber, msg);
				}

				_.each(requiredColumns, function(col) {
					if(row[col] === "") {
						iss("Warning", col + " must not be blank (sighting will not be imported)");
					}
				});
				if(!_.contains(speciesNames, row.Species.toLowerCase())) {
					iss("Warning", "Unrecognized species: " + row.Species);
				}
				if(row.BreedingActivity && !_.contains(breedingActivities, row.BreedingActivity)) {
					iss("Warning", "Unrecognized breeding activity: " + row.BreedingActivity);
				}
				if(!util.isInt(row.Count) && row.Count != 'P') {
					iss("Warning", "Count must be a number or 'P'");
				}
				if(row.Count == '0') {
					iss("Warning", "Sighting with Count = 0 will not be imported");
				}

				if(!allowMultipleSightingsPerSpecies && row.SurveyID && row.Species) {
					var id = row.SurveyID + "_" + row.Species;
					if(seenIDs.hasOwnProperty(id)) {
						iss("Warning", "Species '" + row.Species + "' is included twice for the same SurveyID (sighting will not be imported)");
					}
					seenIDs[id] = true;
				}
			});
		}

		_.chain(rowIssues).values().sortBy('row').each(function(issue) {
			var message = "Row " + (issue.row + 1) + ": " + issue.message;
			if(issue.count > 1) {
				message = message + " (" + issue.count + " occurences in file)";
			}
			issues.push({
				message: message,
				type: issue.type
			});
		});

		_.each(issues, function(i) {
			i.message = fileModel.file.name + ": " + i.message;
		});

		return issues;
	}

	function checkBirdataSurveyFiles(files) {
		return Promise.all(
			_.map(files, function(f) {
				return parseCSV(f.file).then(function(result) {
					var isSighting = containsProperties(result.data[0], ['SurveyID', 'Species']);
					var isSurvey = !isSighting && containsProperties(result.data[0], ['SurveyID', 'StartDate']);
					f.message(isSighting ? "Sighting file detected" : isSurvey ? "Survey file detected" : "SurveyID, Species, or StartDate column missing");
					return {
						isSighting: isSighting,
						isSurvey: isSurvey,
						fileModel: f,
						file: f.file,
						parseResult: result
					};
				}).catch(function(e) {
					console.log(e);
					return {
						fileModel: f,
						file: f.file,
					};
				});
			})
		).then(function(items) {
			if(items.length != 2) {
				return [{
					message: "Exactly two files must be selected - one sightings & one surveys",
					type: "Error"
				}];
			}

			var issues = [];

			var sightingFile, surveyFile;
			_.each(items, function(item) {
				if(item.isSighting) { sightingFile = item; }
				if(item.isSurvey) { surveyFile = item; }
				if(!item.parseResult) {
					issues.push({
						message: "Could not read CSV data in " + item.file.name,
						type: "Error"
					});
				}
			});

			if(!sightingFile) {
				issues.push({ message: "No sighting file detected", type: "Error" });
			}
			if(!surveyFile) {
				issues.push({ message: "No survey file detected", type: "Error" });
			}

			if(issues.length) {
				return issues;
			}

			_.each(items, function(item) {
				if(item.parseResult.data.length == 0) {
					issues.push({ message: "No data in file " + item.file.name, type: "Error" });
				}
			});

			if(issues.length) {
				return issues;
			}

			// OK, we have a sighting file and a survey file with data in it. Now we can do more extensive checks.
			issues = issues.concat(
				checkSightingFile(sightingFile),
				checkSurveyFile(surveyFile));

			return issues;
		});
	}

	function checkFiles(importType, files) {
		if(_.any(files, function(f) { return !f.file })) {
			return Promise.resolve([]);
		}

		if(importType && files.length) {
			switch(importType.code) {
				case 'surveys-birdata':
				case 'surveys-sop':
				case 'surveys-s2020':
				case 'surveys-bnb':
				case 'surveys-abc':
					return checkBirdataSurveyFiles(files);
			}
		}

		return Promise.resolve([]);
	}

	this.checkFiles = checkFiles;
}

export default SurveyImporter;
